import type { SnakeDir } from "./engine";

/**
 * «مار» — WHERE THE BOARD AND THE PAD GO, as a pure function of the usable box.
 *
 * The board is a fixed grid (the same cells on every phone, only scaled), so the only questions are
 * how big a cell is and how big a button is, and the operator's answer is: both on screen together,
 * nothing scrolled, every button easy to hit with a thumb without touching its neighbour.
 *
 * ⚠️ A CELL IS A WHOLE NUMBER OF DEVICE PIXELS (`px`), so the painter's tiles meet without a seam and
 * every repaint lands exactly on the pixels the last one covered. Its CSS size (`cell = px / dpr`) is
 * fractional at most ratios: a 360-px phone at DPR 3 gets 29 device pixels a cell, 9⅔ CSS px.
 *
 *  - THE BUTTONS come from the viewport: about a fifth of its short side, held in 64–80 px on a phone
 *    (a fine pointer — a Desktop mouse — aims at 48–64). Never below 48 px (40 with a fine pointer).
 *  - THE BOARD GIVES WAY FIRST, a whole cell at a time, down to {@link SNAKE_MIN_CELL}; only then do
 *    the buttons shrink below their aim — a pixel at a time, to their floor — to win the board room.
 *  - TWO ARRANGEMENTS: the pad under the board, or beside it (on a wide, short viewport — a phone on
 *    its side). Whichever gives the bigger board wins; a tie keeps the pad under it.
 *  - Every gap — between two buttons, and between the board and the pad — is at least 8 px.
 *
 * The geometry is LEFT-TO-RIGHT and so is the screen that draws it (`dir="ltr"`): under the app's RTL
 * root a grid or a flex row would mirror it, and «left» would land on the right (CLAUDE.md, «A CSS
 * GRID INHERITS dir="rtl"»). The screen places every piece absolutely from these rects.
 */

/** The header band's height under the top inset: 10 px of air and the 40 px exit/score row. */
export const SNAKE_BAND_PX = 50;
/** Air around the board-and-pad group, inside the usable box. */
export const SNAKE_MARGIN_PX = 8;
/** The board's frame, on each side of the cells. */
export const SNAKE_FRAME_PX = 6;
/** The smallest cell the board shrinks to before the buttons give way, CSS px — the 34-cell board as
 *  big as the 17-cell board's 12-px floor made it. */
export const SNAKE_MIN_CELL = 6;
/** The largest cell, CSS px — a big window does not need a giant board. */
export const SNAKE_MAX_CELL = 20;
/** The smallest gap anywhere in the group. */
export const SNAKE_MIN_GAP = 8;

/** A button's size: the aim's range and the floor, by pointer. */
export const SNAKE_BUTTON = {
  coarse: { aimMin: 64, aimMax: 80, floor: 48, share: 0.2 },
  fine: { aimMin: 48, aimMax: 64, floor: 40, share: 0.15 },
} as const;

export interface SnakeRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface SnakeLayoutInput {
  /** The usable box, CSS px: below the header band, inside every safe-area inset. */
  width: number;
  height: number;
  /** Cells per side. */
  grid: number;
  /** A fine pointer (a mouse): `(pointer: fine)`. */
  fine: boolean;
  /** Device pixels per CSS px (`devicePixelRatio`). */
  dpr: number;
}

export interface SnakeLayout {
  arrangement: "stack" | "side";
  /** Device pixels per cell — a whole number. */
  px: number;
  /** CSS px per cell: `px / dpr`. */
  cell: number;
  /** The device pixel ratio it was laid out for. */
  dpr: number;
  /** The board with its frame, in the box's coordinates. */
  board: SnakeRect;
  frame: number;
  button: number;
  gap: number;
  /** The four buttons of the «+», in the box's coordinates. */
  buttons: Record<SnakeDir, SnakeRect>;
}

interface Fit {
  arrangement: "stack" | "side";
  button: number;
  gap: number;
  pad: number;
  /** Device pixels per cell. */
  px: number;
  /** CSS px per cell. */
  cell: number;
}

const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));

/** The gap that goes with a button: an eighth of it, never under 8 px. */
const gapFor = (button: number): number => Math.max(SNAKE_MIN_GAP, Math.round(button / 8));

export function layoutSnake({ width, height, grid, fine, dpr }: SnakeLayoutInput): SnakeLayout {
  const ratio = dpr > 0 && Number.isFinite(dpr) ? dpr : 1;
  const maxPx = Math.floor(SNAKE_MAX_CELL * ratio);
  const rule = fine ? SNAKE_BUTTON.fine : SNAKE_BUTTON.coarse;
  const aim = clamp(Math.round(Math.min(width, height) * rule.share), rule.aimMin, rule.aimMax);
  const innerW = width - 2 * SNAKE_MARGIN_PX;
  const innerH = height - 2 * SNAKE_MARGIN_PX;

  const fit = (arrangement: "stack" | "side", button: number): Fit => {
    const gap = gapFor(button);
    const pad = 3 * button + 2 * gap;
    const stack = arrangement === "stack";
    const roomW = stack ? innerW : innerW - pad - gap;
    const roomH = stack ? innerH - pad - gap : innerH;
    const padFits = stack ? pad <= innerW : pad <= innerH;
    // the biggest whole number of device pixels a cell can have (a hair of rounding error spared)
    const px = padFits
      ? clamp(
          Math.floor(((Math.min(roomW, roomH) - 2 * SNAKE_FRAME_PX) * ratio) / grid + 1e-9),
          0,
          maxPx,
        )
      : 0;
    return { arrangement, button, gap, pad, px, cell: px / ratio };
  };

  // The board shrinks first; below the smallest cell, the buttons give way down to their floor.
  const best = (arrangement: "stack" | "side"): Fit => {
    let button = aim;
    while (button > rule.floor && fit(arrangement, button).cell < SNAKE_MIN_CELL) button--;
    return fit(arrangement, button);
  };
  const stack = best("stack");
  const side = best("side");
  const pick = side.cell > stack.cell ? side : stack;

  const { arrangement, button, gap, pad, px, cell } = pick;
  const outer = (px * grid) / ratio + 2 * SNAKE_FRAME_PX;
  const groupW = arrangement === "stack" ? Math.max(outer, pad) : outer + gap + pad;
  const groupH = arrangement === "stack" ? outer + gap + pad : Math.max(outer, pad);
  const x0 = Math.floor((width - groupW) / 2);
  const y0 = Math.floor((height - groupH) / 2);
  const board: SnakeRect =
    arrangement === "stack"
      ? { x: x0 + Math.floor((groupW - outer) / 2), y: y0, w: outer, h: outer }
      : { x: x0, y: y0 + Math.floor((groupH - outer) / 2), w: outer, h: outer };
  const padX = arrangement === "stack" ? x0 + Math.floor((groupW - pad) / 2) : x0 + outer + gap;
  const padY = arrangement === "stack" ? y0 + outer + gap : y0 + Math.floor((groupH - pad) / 2);
  const step = button + gap;
  const at = (col: number, row: number): SnakeRect => ({
    x: padX + col * step,
    y: padY + row * step,
    w: button,
    h: button,
  });
  return {
    arrangement,
    px,
    cell,
    dpr: ratio,
    board,
    frame: SNAKE_FRAME_PX,
    button,
    gap,
    buttons: { up: at(1, 0), left: at(0, 1), right: at(2, 1), down: at(1, 2) },
  };
}
